import React, { useState, useMemo, useEffect } from "react";
import Modal from "../Modal";
import styled from '@emotion/styled';
import { Alert } from "../Alert";
import { isMobile } from 'react-device-detect';
import { StyledModal } from "../../StyledComponents/Modals/Modals";
import { fetchFrameTypes, fetchFrameLists, createSample } from "../../../http/samples";
import SelectWithLabel from "../../Inputs/Select";
import Table, { SelectColumnFilter } from "../Table";
import { FieldContainer } from "../../StyledComponents/Forms/Forms";
import TextInputWithLabel from "../../Inputs/TextInput";
import Card from "../Card";
import { CancelButton } from "../../StyledComponents/Buttons/Buttons";

const ModalFooter = styled.div`
  text-align: right;
  flex-shrink: 0;
  padding: 10px 0px;
`;

const ModalBody = styled.div`
  max-height: ${isMobile ? '500px' : '720px'};
  overflow-y: auto;
  width:100%;

  padding: 0px 0px ${isMobile ? "0px" : "30px"} 0px;

  flex-grow: 1;
  & > div { 
    margin-top: 0;
  }
`;


const Button = styled.button`
  background-color: #93C572;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 10px 20px;
  margin-right: 10px;

  &:hover {
    background-color: #698D51;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const HoveredImageContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 30%;
  transform: translateY(-50%);
  z-index: 1000;
  pointer-events: none;
`;

const HoveredImage = styled.img`
  max-width: 500px;
  max-height: 500px;
`;

const defaultFrameRequest = {
  sample: {
    optunique: null,
    patient: null
  },
  lstFrames: []
}

export default function FittingFramesModal({
  showHideModal,
  isModalOpen,
  colour,
  opticiansId,
  fnSuccess,
  fnError
}) {
  const [selectedFrameType, setSelectedFrameType] = useState('All');
  const [hoveredImage, setHoveredImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [frames, setFrames] = useState([]);
  const [missingFields, setMissingFields] = useState({});
  const [frameTypes, setFrameTypes] = useState([]);
  const [frameRequestData, setFrameRequestData] = useState({
    ...defaultFrameRequest
  });

  useEffect(() => {
    const fetchFrames = async () => {
      setIsLoading(true);
      try {
        const frames = await fetchFrameLists(selectedFrameType);
        setFrames(frames);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFrames();
  }, [isModalOpen]);

  const isFrameSelected = (frameId) => {
    return frameRequestData.lstFrames.some(selectedFrame => parseInt(selectedFrame.FrameId) === parseInt(frameId));
  };

  const selectFrame = (frameId) => {
    if (isFrameSelected(frameId)) {
      setFrameRequestData(prevState => ({
        ...prevState,
        lstFrames: prevState.lstFrames.filter(frame => parseInt(frame.FrameId) !== parseInt(frameId))
      }));
    } else {
      setFrameRequestData(prevState => ({
        ...prevState,
        lstFrames: [...prevState.lstFrames, { FrameId: parseInt(frameId) }]
      }));
    }
  }

  const framesColumns = React.useMemo(() => [
    {
      id: 'selected',
      accessor: 'frameId',
      Cell: ({ value }) => (
        <input
          type="checkbox"
          disabled={frameRequestData.lstFrames.length >= 4 && !isFrameSelected(value)}
          checked={isFrameSelected(value)}
          onChange={() => selectFrame(value)}
        />
      ),
      Header: () => <div style={{ width: '30px' }}> </div>,
      width: 30,
    },
    {
      Header: "Name",
      accessor: "frameName",
    },
    {
      Header: "Material",
      accessor: "frameMaterial",
    },
    {
      accessor: "ImageByte",
      Cell: ({ row }) => {
        const imageByte = row.original.ImageByte;
        const urlDirect = row.original.ImageUrl;

        return (
          <img
            src={`data:image/jpeg;base64,${imageByte}`}
            alt="Frame"
            style={{ maxWidth: '50px', maxHeight: '50px', objectFit: 'contain', cursor: 'pointer' }}
            onMouseEnter={() => setHoveredImage(imageByte)}
            onMouseLeave={() => setHoveredImage(null)}
            onClick={() => window.open(urlDirect)}
          />);
      },
      Header: () => <div style={{ width: '50px' }}> </div>,
      width: 20,
    },
    {
      Header: "Type",
      accessor: "frametype",
      Filter: SelectColumnFilter,
      filter: 'includes',
    }
  ], [frames, frameRequestData]);

  const handleChange = (field, value) => {
    setFrameRequestData(prev => ({ ...prev, sample: { ...prev.sample, [field]: value } }));
  };

  function validateForm() {
    setError(null);
    if (!frameRequestData.sample.patient || frameRequestData.sample.patient.trim() === '') {
      setError('Patient name is required');
      return false;
    }

    if (frameRequestData.lstFrames.length === 0) {
      setError('At least one frame must be selected');
      return false;
    }

    return true;
  }

  async function save() {
    if (!validateForm()) {
      return;
    }

    const formattedRequest = {
      ...frameRequestData,
      sample: {
        ...frameRequestData.sample,
        optunique: opticiansId
      }
    }

    setIsLoading(true);
    try {
      await createSample(formattedRequest);
      setError(null);
      fnSuccess(formattedRequest.sample.patient);
    } catch (e) {
      setError(e);
      fnError(e);
    } finally {
      setIsLoading(false);
      showHideModal(false);
    }
  }

  return (
    <Modal open={isModalOpen}>
      <StyledModal>
        <Card label="Fitting Frame Request" headercolour={colour.sidebar} headerTextColour={colour.font}>
          <ModalBody>
            <FieldContainer>
              <TextInputWithLabel
                label='Patient Name'
                value={frameRequestData.sample.patient}
                required={true}
                onChange={e => handleChange('patient', e.target.value)} />
            </FieldContainer>
            <Table
              columns={framesColumns}
              data={frames}
              defaultPageSize={5} />
            {hoveredImage && (
              <HoveredImageContainer>
                <HoveredImage
                  src={`data:image/jpeg;base64,${hoveredImage}`}
                  alt="Enlarged Frame"
                />
              </HoveredImageContainer>
            )}
          </ModalBody>
          <ModalFooter>
            {error && <Alert type='error'>{error}</Alert>}
            {isLoading && <Alert type='information'>Loading..</Alert>}
            <CancelButton onClick={() => showHideModal(false)}>Close</CancelButton>
            <Button onClick={() => save()}>Submit Request</Button>
          </ModalFooter>
        </Card>
      </StyledModal>
    </Modal>
  );
}
